import type {
  LayoutDocument,
  LayoutNode,
  LayoutNodeStyle,
} from "../layout/types.js";
import { validateLayout } from "../layout/validation.js";

export type CustomWebControl = {
  key: string;
  kind: "TITLE" | "SEARCH" | "BUTTON" | "TOGGLE";
  label: string;
  visible: boolean;
  /** These commands only change the spectator's local view. */
  action:
    | "REFRESH"
    | "RESET_VIEW"
    | "CLEAR_SEARCH"
    | "VIEW_MODE"
    | "FOLLOW_CURRENT"
    | "INPUT";
  inputKey: string;
  defaultValue: string;
  options: { value: string; label: string; icon: string | null }[];
  searchFields: string[];
  icon: string | null;
  variant: "text" | "outlined" | "contained";
  size: "small" | "medium" | "large";
  color:
    | "inherit"
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "info"
    | "warning";
  width: number | null;
};
export type CustomWebToolbar = {
  controls: CustomWebControl[];
  presentation: LayoutDocument;
};

export function createCustomWebControl(
  kind: CustomWebControl["kind"],
  key: string,
): CustomWebControl {
  return {
    key,
    kind,
    label:
      kind === "SEARCH"
        ? "Rechercher un athlète"
        : kind === "TITLE"
          ? "Groupe"
          : kind === "TOGGLE"
            ? "Vue d’affichage"
            : "Actualiser la vue",
    visible: true,
    action: kind === "TOGGLE" ? "VIEW_MODE" : "REFRESH",
    inputKey: "",
    defaultValue: "",
    options:
      kind === "TOGGLE"
        ? [
            { value: "CATEGORY", label: "Catégories", icon: "ViewList" },
            { value: "LIVE", label: "Direct", icon: "Scale" },
          ]
        : [],
    searchFields: [],
    icon: kind === "BUTTON" ? "Refresh" : null,
    variant: "text",
    size: "medium",
    color: "primary",
    width: kind === "TOGGLE" ? 300 : kind === "SEARCH" ? 240 : null,
  };
}

export function createCustomWebToolbar(): CustomWebToolbar {
  const toolbar = {
    controls: [
      createCustomWebControl("TITLE", "title"),
      createCustomWebControl("TOGGLE", "mode"),
    ],
  };
  return {
    ...toolbar,
    presentation: createCustomWebToolbarPresentation(toolbar.controls),
  };
}

/** Preserve spectator commands on views saved before the toolbar was configurable. */
export function createPreviousCustomWebToolbar(): CustomWebToolbar {
  const controls = [
    createCustomWebControl("TITLE", "title"),
    createCustomWebControl("SEARCH", "search"),
    createCustomWebControl("TOGGLE", "mode"),
    {
      ...createCustomWebControl("TOGGLE", "follow"),
      label: "Suivre l’athlète en cours",
      action: "FOLLOW_CURRENT" as const,
      options: [
        { value: "false", label: "Suivi désactivé", icon: null },
        {
          value: "true",
          label: "Suivre l’athlète en cours",
          icon: "MyLocation",
        },
      ],
    },
    createCustomWebControl("BUTTON", "refresh"),
  ];
  return {
    controls,
    presentation: createCustomWebToolbarPresentation(controls),
  };
}

/** Stable style samples, as for the grid. Layout stays responsive rather than using canvas coordinates. */
export function createCustomWebToolbarPresentation(
  controls: CustomWebControl[],
  previous?: LayoutDocument,
): LayoutDocument {
  const samples: { id: string; label: string; style: LayoutNodeStyle }[] = [
    {
      id: "toolbar",
      label: "Barre de commandes",
      style: {
        backgroundColor: "rgba(255,255,255,0.7)",
        borderRadius: 4,
        padding: 8,
      },
    },
  ];
  for (const control of controls) {
    const isToggle = control.kind === "TOGGLE";
    samples.push({
      id: `control:${control.key}`,
      label: control.label,
      style:
        control.kind === "TITLE"
          ? { fontSize: 22, fontWeight: "700" }
          : { borderRadius: isToggle ? 50 : 4, fontSize: 14 },
    });
    if (isToggle)
      for (const option of control.options)
        samples.push({
          id: `control:${control.key}:option:${option.value}`,
          label: option.label,
          style: {},
        });
    if (control.kind === "BUTTON" || isToggle)
      for (const state of ["hover", "selected", "disabled"])
        samples.push({
          id: `control:${control.key}:${state}`,
          label: `${control.label} · ${state}`,
          style:
            state === "selected"
              ? { backgroundColor: "#0056B3", color: "#ffffff" }
              : state === "hover"
                ? { backgroundColor: "rgba(25,118,210,0.08)" }
                : { opacity: 0.5 },
        });
  }
  const old = new Map<string, LayoutNode>();
  const visit = (node: LayoutNode) => {
    old.set(node.id, node);
    if ("children" in node) node.children.forEach(visit);
  };
  previous?.scene.children.forEach(visit);
  return {
    version: 1,
    meta: {
      id: "web-toolbar-presentation",
      name: "Barre de commandes",
      type: "display",
      canvas: { width: 600, height: 20 + samples.length * 76 },
      createdAt: "2026-10-22T00:00:00.000Z",
      updatedAt: "2026-10-22T00:00:00.000Z",
    },
    scene: {
      backgroundColor: "#f5f5f5",
      children: samples.map((sample, index) => ({
        id: sample.id,
        label: sample.label,
        kind: "primitive",
        primitive: "text",
        text: sample.label,
        x: 24,
        y: 20 + index * 76,
        width: 552,
        height: 52,
        zIndex: 0,
        style: old.get(sample.id)?.style ?? sample.style,
      })),
    },
  };
}

const styleKeys = new Set([
  "backgroundColor",
  "backgroundImage",
  "color",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "textAlign",
  "textTransform",
  "whiteSpace",
  "textShadow",
  "textDecorationLine",
  "textDecorationColor",
  "textDecorationStyle",
  "textDecorationThickness",
  "border",
  "borderTop",
  "borderRight",
  "borderBottom",
  "borderLeft",
  "borderColor",
  "borderRadius",
  "borderTopLeftRadius",
  "borderTopRightRadius",
  "borderBottomLeftRadius",
  "borderBottomRightRadius",
  "padding",
  "paddingLeft",
  "paddingRight",
  "paddingTop",
  "paddingBottom",
  "margin",
  "marginTop",
  "marginRight",
  "marginBottom",
  "marginLeft",
  "cursor",
  "filter",
  "boxShadow",
  "opacity",
  "height",
  "minHeight",
  "gap",
]);
export function customWebToolbarStyles(document: LayoutDocument) {
  const styles: Record<string, Record<string, unknown>> = {};
  const visit = (node: LayoutNode) => {
    styles[node.id] = Object.fromEntries(
      Object.entries(node.style ?? {})
        .filter(([key]) => styleKeys.has(key))
        .map(([key, value]) => [
          key,
          typeof value === "number" &&
          /^padding|^margin|^border.*Radius$|^gap$/.test(key)
            ? `${value}px`
            : value,
        ]),
    );
    if ("children" in node) node.children.forEach(visit);
  };
  document.scene.children.forEach(visit);
  return styles;
}

const safeKey = (value: unknown): value is string =>
  typeof value === "string" &&
  /^[a-zA-Z][a-zA-Z0-9_-]{0,79}$/.test(value) &&
  !["constructor", "prototype", "__proto__"].includes(value);
export function validateCustomWebToolbar(value: unknown): boolean {
  try {
    const t = value as CustomWebToolbar;
    if (
      !t ||
      !validateLayout(t.presentation).valid ||
      !Array.isArray(t.controls) ||
      t.controls.length > 40
    )
      return false;
    const keys = new Set<string>();
    for (const c of t.controls) {
      if (
        !c ||
        !safeKey(c.key) ||
        keys.has(c.key) ||
        !["TITLE", "SEARCH", "BUTTON", "TOGGLE"].includes(c.kind) ||
        typeof c.label !== "string" ||
        !c.label.trim() ||
        typeof c.visible !== "boolean" ||
        ![
          "REFRESH",
          "RESET_VIEW",
          "CLEAR_SEARCH",
          "VIEW_MODE",
          "FOLLOW_CURRENT",
          "INPUT",
        ].includes(c.action) ||
        typeof c.inputKey !== "string" ||
        typeof c.defaultValue !== "string" ||
        !Array.isArray(c.options) ||
        c.options.length > 20 ||
        !Array.isArray(c.searchFields) ||
        c.searchFields.some((f) => typeof f !== "string") ||
        !["text", "outlined", "contained"].includes(c.variant) ||
        !["small", "medium", "large"].includes(c.size) ||
        ![
          "inherit",
          "primary",
          "secondary",
          "success",
          "error",
          "info",
          "warning",
        ].includes(c.color) ||
        (c.width !== null &&
          (!Number.isFinite(c.width) || c.width < 80 || c.width > 1200)) ||
        (c.icon !== null && typeof c.icon !== "string")
      )
        return false;
      if (
        c.action === "INPUT" &&
        (!safeKey(c.inputKey) ||
          ["viewMode", "followCurrentAthlete"].includes(c.inputKey))
      )
        return false;
      if (
        c.kind === "TOGGLE" &&
        !["VIEW_MODE", "FOLLOW_CURRENT", "INPUT"].includes(c.action)
      )
        return false;
      if (
        c.kind === "BUTTON" &&
        !["REFRESH", "RESET_VIEW", "CLEAR_SEARCH", "INPUT"].includes(c.action)
      )
        return false;
      const values = new Set<string>();
      for (const o of c.options) {
        if (
          !o ||
          typeof o.value !== "string" ||
          !o.value ||
          values.has(o.value) ||
          typeof o.label !== "string" ||
          !o.label.trim() ||
          (o.icon !== null && typeof o.icon !== "string")
        )
          return false;
        values.add(o.value);
      }
      if (
        c.kind === "TOGGLE" &&
        (values.size < 2 ||
          (c.action === "VIEW_MODE" &&
            (values.size !== 2 ||
              !values.has("LIVE") ||
              !values.has("CATEGORY"))) ||
          (c.action === "FOLLOW_CURRENT" &&
            (values.size !== 2 ||
              !values.has("true") ||
              !values.has("false"))) ||
          (c.action === "INPUT" && !values.has(c.defaultValue)))
      )
        return false;
      keys.add(c.key);
    }
    return true;
  } catch {
    return false;
  }
}

/** Primitive local inputs are the only values exposed to read-only Behavior graphs. */
export function customWebToolbarInputs(
  toolbar: CustomWebToolbar,
): Record<string, string> {
  return Object.fromEntries(
    toolbar.controls
      .filter((c) => c.visible && c.action === "INPUT")
      .map((c) => [c.inputKey, c.defaultValue]),
  );
}

export function filterCustomWebRows<T extends Record<string, unknown>>(
  rows: T[],
  search: string,
  toolbar: CustomWebToolbar,
): T[] {
  const controls = toolbar.controls.filter(
    (c) => c.kind === "SEARCH" && c.visible,
  );
  const needle = search.trim().toLocaleLowerCase();
  if (!needle || !controls.length) return rows;
  const fields = [...new Set(controls.flatMap((c) => c.searchFields))];
  return rows.filter((row) =>
    (fields.length ? fields.map((key) => row[key]) : Object.values(row)).some(
      (v) => {
        const text =
          v && typeof v === "object" ? (v as { name?: unknown }).name : v;
        return (
          (typeof text === "string" || typeof text === "number") &&
          String(text).toLocaleLowerCase().includes(needle)
        );
      },
    ),
  );
}
